import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Phone, Mail, Trash2, ShieldCheck, Bell, AlertTriangle, CalendarRange, XCircle, Send, ChevronDown } from 'lucide-react';
import { BranchStaff, StaffRole, StaffPermissions, StaffNotifications, staffRoles, roleMeta, permissionMeta, notificationMeta, defaultPermissionsForRole, defaultNotificationsForRole, avatarColorMap, roleColorMap } from './BranchesContext';
import { Toggle } from './Toggle';
const notifIcons: Record<string, ComponentType<{
  size?: number;
}>> = {
  bell: Bell,
  alert: AlertTriangle,
  x: XCircle,
  summary: CalendarRange
};
export function StaffEditorModal({
  staff,
  isNew,
  onClose,
  onSave,
  onDelete
}: {
  staff: BranchStaff;
  isNew: boolean;
  onClose: () => void;
  onSave: (patch: Partial<BranchStaff>) => void;
  onDelete?: () => void;
}) {
  const [draft, setDraft] = useState<BranchStaff>(staff);
  const [testSentTo, setTestSentTo] = useState<string | null>(null);
  useEffect(() => {
    setDraft(staff);
  }, [staff.id]);
  const setField = <K extends keyof BranchStaff,>(key: K, value: BranchStaff[K]) => setDraft(d => ({
    ...d,
    [key]: value
  }));
  const setRole = (role: StaffRole) => {
    setDraft(d => ({
      ...d,
      role,
      permissions: defaultPermissionsForRole(role),
      notifications: defaultNotificationsForRole(role)
    }));
  };
  const setPerm = (key: keyof StaffPermissions, value: boolean) => {
    setDraft(d => ({
      ...d,
      permissions: {
        ...d.permissions,
        [key]: value
      }
    }));
  };
  const setNotif = (key: keyof StaffNotifications, value: boolean) => {
    setDraft(d => ({
      ...d,
      notifications: {
        ...d.notifications,
        [key]: value
      }
    }));
  };
  const canSubmit = draft.firstName.trim().length > 0 && draft.phone.trim().length > 0;
  const handleSave = () => {
    if (!canSubmit) return;
    onSave(draft);
    onClose();
  };
  const sendTestSMS = () => {
    if (!draft.phone.trim()) return;
    setTestSentTo(draft.phone);
    setTimeout(() => setTestSentTo(null), 2400);
  };
  const initials = (draft.firstName[0] || '').toUpperCase() + (draft.lastName[0] || '').toUpperCase();
  const roleColor = roleColorMap[roleMeta[draft.role].color];
  return <AnimatePresence data-id="element-3610">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-id="element-3611">
        <motion.div initial={{
        y: 30,
        opacity: 0,
        scale: 0.98
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        y: 30,
        opacity: 0,
        scale: 0.98
      }} transition={{
        type: 'spring',
        stiffness: 320,
        damping: 30
      }} className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-3612">
          {/* Header */}
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0" data-id="element-3613">
            <div className="flex items-center gap-3 min-w-0" data-id="element-3614">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${avatarColorMap[draft.avatarColor] || avatarColorMap.orange}`} data-id="element-3615">
                {initials || <User size={16} data-id="element-3616" />}
              </div>
              <div className="min-w-0" data-id="element-3617">
                <h2 className="font-bold text-gray-900" data-id="element-3618">
                  {isNew ? 'Add team member' : 'Edit team member'}
                </h2>
                <p className="text-xs text-gray-500 truncate" data-id="element-3619">
                  {isNew ? 'Invite someone to manage this branch' : `${draft.firstName} ${draft.lastName}`.trim() || 'Untitled member'}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500" data-id="element-3620">
              <X size={18} data-id="element-3621" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6" data-id="element-3622">
            {/* Identity */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-3623">
              <FieldInput label="First name" icon={<User size={12} data-id="element-3625" />} value={draft.firstName} onChange={v => setField('firstName', v)} placeholder="Selam" data-id="element-3624" />
              <FieldInput label="Last name" value={draft.lastName} onChange={v => setField('lastName', v)} placeholder="Tadesse" data-id="element-3626" />
              <FieldInput label="Phone (for SMS)" icon={<Phone size={12} data-id="element-3628" />} value={draft.phone} onChange={v => setField('phone', v)} placeholder="+251 9XX XXX XXX" required data-id="element-3627" />
              <FieldInput label="Email" icon={<Mail size={12} data-id="element-3630" />} value={draft.email || ''} onChange={v => setField('email', v)} placeholder="optional" data-id="element-3629" />
            </div>

            {/* Role + active */}
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end" data-id="element-3631">
              <div data-id="element-3632">
                <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-3633">
                  Role
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-id="element-3634">
                  {staffRoles.map(r => {
                  const meta = roleMeta[r];
                  const active = draft.role === r;
                  const c = roleColorMap[meta.color];
                  return <button key={r} onClick={() => setRole(r)} className={`text-left p-2.5 rounded-lg border text-xs transition-all ${active ? `${c.bg} ${c.text} border-transparent ring-2 ${c.ring}` : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'}`} data-id="element-3635">
                        <div className="font-bold leading-tight" data-id="element-3636">
                          {meta.label}
                        </div>
                        <div className={`text-[10px] mt-0.5 leading-tight ${active ? '' : 'text-gray-500'}`} data-id="element-3637">
                          {meta.description}
                        </div>
                      </button>;
                })}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 pb-1" data-id="element-3638">
                <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide" data-id="element-3639">
                  Active
                </span>
                <Toggle checked={draft.active} onChange={v => setField('active', v)} data-id="element-3640" />
              </div>
            </div>

            {/* Permissions */}
            <div data-id="element-3641">
              <SectionHeader icon={<ShieldCheck size={14} className="text-emerald-600" data-id="element-3643" />} title="Branch permissions" description="What this team member can do at this branch" data-id="element-3642" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3" data-id="element-3644">
                {permissionMeta.map(p => {
                const checked = draft.permissions[p.key];
                return <label key={p.key} className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors ${checked ? 'border-emerald-200 bg-emerald-50/50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-3645">
                      <input type="checkbox" checked={checked} onChange={e => setPerm(p.key, e.target.checked)} className="mt-0.5 w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 border-gray-300" data-id="element-3646" />
                      <div className="min-w-0" data-id="element-3647">
                        <div className="text-xs font-bold text-gray-900" data-id="element-3648">
                          {p.label}
                        </div>
                        <div className="text-[11px] text-gray-500 leading-snug" data-id="element-3649">
                          {p.description}
                        </div>
                      </div>
                    </label>;
              })}
              </div>
            </div>

            {/* Notifications */}
            <div data-id="element-3650">
              <SectionHeader icon={<Bell size={14} className="text-orange-600" data-id="element-3652" />} title="SMS notification routing" description="Choose which alerts get sent to this number" data-id="element-3651" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3" data-id="element-3653">
                {notificationMeta.map(n => {
                const I = notifIcons[n.icon] || Bell;
                const checked = draft.notifications[n.key];
                return <div key={n.key} className={`flex items-start gap-2.5 p-3 rounded-lg border transition-colors ${checked ? 'border-orange-200 bg-orange-50/60' : 'border-gray-200 bg-white'}`} data-id="element-3654">
                      <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${checked ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-400'}`} data-id="element-3655">
                        <I size={14} data-id="element-3656" />
                      </div>
                      <div className="min-w-0 flex-1" data-id="element-3657">
                        <div className="flex items-center justify-between gap-2" data-id="element-3658">
                          <div className="text-xs font-bold text-gray-900" data-id="element-3659">
                            {n.label}
                          </div>
                          <Toggle checked={checked} onChange={v => setNotif(n.key, v)} data-id="element-3660" />
                        </div>
                        <div className="text-[11px] text-gray-500 leading-snug mt-0.5" data-id="element-3661">
                          {n.description}
                        </div>
                      </div>
                    </div>;
              })}
              </div>

              {/* Test SMS */}
              <div className="mt-3 flex items-center justify-between gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200" data-id="element-3662">
                <div className="text-xs text-gray-600 leading-snug" data-id="element-3663">
                  Send a one-time test SMS to{' '}
                  <span className="font-semibold text-gray-900" data-id="element-3664">
                    {draft.phone || '—'}
                  </span>{' '}
                  to confirm delivery.
                </div>
                <button onClick={sendTestSMS} disabled={!draft.phone.trim()} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0" data-id="element-3665">
                  <Send size={12} data-id="element-3666" />
                  Send test
                </button>
              </div>
              <AnimatePresence data-id="element-3667">
                {testSentTo && <motion.div initial={{
                opacity: 0,
                y: -4
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0
              }} className="mt-2 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1.5" data-id="element-3668">
                    ✓ Test SMS queued to {testSentTo}
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>

          {/* Footer */}
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between gap-3 flex-shrink-0 bg-gray-50" data-id="element-3669">
            <div data-id="element-3670">
              {!isNew && onDelete && <button onClick={() => {
              onDelete();
              onClose();
            }} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-rose-600 hover:bg-rose-50 text-xs font-semibold transition-colors" data-id="element-3671">
                  <Trash2 size={13} data-id="element-3672" />
                  Remove
                </button>}
            </div>
            <div className="flex items-center gap-2" data-id="element-3673">
              <button onClick={onClose} className="px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-100 text-sm font-semibold transition-colors" data-id="element-3674">
                Cancel
              </button>
              <button onClick={handleSave} disabled={!canSubmit} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm" data-id="element-3675">
                {isNew ? 'Add member' : 'Save changes'}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}
function FieldInput({
  label,
  icon,
  value,
  onChange,
  placeholder,
  required
}: {
  label: string;
  icon?: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
}) {
  return <label className="block" data-id="element-3676">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-3677">
        {icon}
        {label}
        {required && <span className="text-orange-500" data-id="element-3678">*</span>}
      </div>
      <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition" data-id="element-3679" />
    </label>;
}
function SectionHeader({
  icon,
  title,
  description
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return <div className="flex items-start gap-2" data-id="element-3680">
      <div className="mt-0.5" data-id="element-3681">{icon}</div>
      <div data-id="element-3682">
        <div className="text-sm font-bold text-gray-900" data-id="element-3683">{title}</div>
        <div className="text-[11px] text-gray-500" data-id="element-3684">{description}</div>
      </div>
    </div>;
}